import {
  createCompanyLocation,
  fetchCompanyLocations,
  updateCompanyLocation,
} from './locationRepository';
import { DEFAULT_LOCATION_NAME, normalizeLocationIds } from './locationTypes';
import {
  listEmployees,
  listServices,
  upsertEmployee,
  upsertService,
} from '../api/companyRecords';
import { listAppointments, upsertAppointment } from '../api/appointments';

const buildDefaultLocationPayload = (companyData = {}) => ({
  name: DEFAULT_LOCATION_NAME,
  address: companyData.businessInfo?.address || companyData.address || {},
  phone: companyData.phone || companyData.businessPhone || '',
  timezone: companyData.businessInfo?.timezone || 'America/New_York',
  businessHours: null,
  active: true,
  isDefault: true,
  sortOrder: 0,
});

export const ensureDefaultLocationForCompany = async (companyId, companyData = null) => {
  if (!companyId) {
    return { migrated: false, locationId: null, locations: [] };
  }

  const existingLocations = await fetchCompanyLocations(companyId);
  if (existingLocations.length > 0) {
    const defaultLocation = existingLocations.find((location) => location.isDefault)
      || existingLocations[0];
    return {
      migrated: false,
      locationId: defaultLocation?.id || null,
      locations: existingLocations,
    };
  }

  const resolvedCompanyData = companyData || {};
  const createdLocation = await createCompanyLocation(
    companyId,
    buildDefaultLocationPayload(resolvedCompanyData)
  );
  const locationId = createdLocation.id;

  await backfillEntityLocationIds({
    companyId,
    locationId,
    appendToExisting: false,
  });

  const appointments = await listAppointments({ companyId, archived: 'all' });
  const appointmentList = appointments?.appointments || appointments || [];
  await Promise.all(
    appointmentList
      .filter((appointment) => appointment?.id && !appointment.locationId)
      .map((appointment) => upsertAppointment({
        companyId,
        id: appointment.id,
        appointment: {
          ...appointment,
          locationId,
          locationName: createdLocation.name || DEFAULT_LOCATION_NAME,
          locationAddress: createdLocation.address || null,
        },
      }))
  );

  const locations = await fetchCompanyLocations(companyId);
  return {
    migrated: true,
    locationId,
    locations,
  };
};

export const backfillEntityLocationIds = async ({
  companyId,
  locationId,
  ownerId,
  appendToExisting = false,
}) => {
  if (!companyId || !locationId) return;

  const [employees, services] = await Promise.all([
    listEmployees({ companyId }),
    listServices({ companyId }),
  ]);

  await Promise.all(
    (employees || []).map(async (employee) => {
      const locationIds = normalizeLocationIds(employee.locationIds);
      let nextLocationIds = locationIds;
      if (appendToExisting) {
        if (!locationIds.includes(locationId)) {
          nextLocationIds = [...locationIds, locationId];
        }
      } else if (locationIds.length === 0) {
        nextLocationIds = [locationId];
      }
      if (nextLocationIds === locationIds) return;
      await upsertEmployee({
        companyId,
        id: employee.id,
        employee: { locationIds: nextLocationIds, updatedAt: new Date().toISOString() },
      });
    })
  );

  await Promise.all(
    (services || []).map(async (service) => {
      const locationIds = normalizeLocationIds(service.locationIds);
      let nextLocationIds = locationIds;
      if (appendToExisting) {
        if (!locationIds.includes(locationId)) {
          nextLocationIds = [...locationIds, locationId];
        }
      } else if (locationIds.length === 0) {
        nextLocationIds = [locationId];
      }
      if (nextLocationIds === locationIds) return;
      await upsertService({
        companyId,
        id: service.id,
        service: { locationIds: nextLocationIds, updatedAt: new Date().toISOString() },
      });
    })
  );
};

export const assignNewLocationToAllEntities = async ({
  companyId,
  locationId,
  ownerId,
}) => backfillEntityLocationIds({
  companyId,
  locationId,
  ownerId,
  appendToExisting: true,
});

export const deactivateCompanyLocation = async (companyId, locationId) => {
  await updateCompanyLocation(companyId, locationId, {
    active: false,
    updatedAt: new Date().toISOString(),
  });
};
